

"use client"

import { useState, useEffect } from "react"
import { AuthLayout } from "@/components/auth/auth-layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { useRouter } from "@/src/hooks/useRouter"
import { Eye, EyeOff, Loader2 } from "lucide-react"
import { requestOtp, createRegistrationSession } from "@/lib/api/registration"
import { saveStep1Data } from "@/lib/utils/registrationSession"
import { useDemoMode } from "@/src/contexts/DemoModeContext"

// Backend ignores the OTP value in demo mode but its validation still expects 6 digits
const DEMO_OTP_PLACEHOLDER = "000000"

export default function RegisterStep2() {
  const router = useRouter()
  const { demoMode } = useDemoMode()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [countdown, setCountdown] = useState(0)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const [formData, setFormData] = useState({
    phoneNumber: "",
    otpCode: "",
    password: "",
    confirmPassword: "",
    agreeTerms: false,
    agreePrivacy: false,
  })

  // Countdown timer for OTP resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setError("") // Clear error when user types
  }

  const handleRequestOTP = async () => {
    setError("")
    setSuccess("")

    // Validate phone number
    if (!formData.phoneNumber.match(/^0[6-9]\d{8}$/)) {
      setError("กรุณากรอกหมายเลขโทรศัพท์ให้ถูกต้อง (10 หลัก, เริ่มต้นด้วย 06-09)")
      return
    }

    try {
      setLoading(true)
      const response = await requestOtp(formData.phoneNumber)
      setSuccess("ส่งรหัส OTP ไปยังหมายเลข " + formData.phoneNumber + " แล้ว")
      setCountdown(response.data.retryAfter || 60)
    } catch (err: any) {
      setError(err.message || "เกิดข้อผิดพลาดในการส่ง OTP")
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    setError("")
    setSuccess("")

    // Validation
    if (!formData.phoneNumber.match(/^0[6-9]\d{8}$/)) {
      setError("กรุณากรอกหมายเลขโทรศัพท์ให้ถูกต้อง")
      return
    }

    if (!demoMode && (!formData.otpCode || formData.otpCode.length !== 6)) {
      setError("กรุณากรอกรหัส OTP 6 หลัก")
      return
    }

    if (!formData.password || formData.password.length < 8) {
      setError("รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร")
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setError("รหัสผ่านและการยืนยันรหัสผ่านไม่ตรงกัน")
      return
    }

    if (!formData.agreeTerms || !formData.agreePrivacy) {
      setError("กรุณายอมรับข้อตกลงและนโยบายความเป็นส่วนตัว")
      return
    }

    try {
      setLoading(true)

      // Create registration session (verifies OTP and generates token)
      const sessionResponse = await createRegistrationSession(
        formData.phoneNumber,
        demoMode ? DEMO_OTP_PLACEHOLDER : formData.otpCode
      )

      // Save to sessionStorage
      saveStep1Data({
        phoneNumber: formData.phoneNumber,
        password: formData.password,
        sessionToken: sessionResponse.data.sessionToken,
        agreeTerms: formData.agreeTerms,
        agreePrivacy: formData.agreePrivacy,
      })

      setSuccess(
        demoMode
          ? "กำลังไปยังขั้นตอนถัดไป..."
          : "ยืนยัน OTP สำเร็จ! กำลังไปยังขั้นตอนถัดไป..."
      )
      
      // Navigate to step 3
      setTimeout(() => {
        router.push("/auth/register/step-3")
      }, 1000)

    } catch (err: any) {
      setError(err.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง")
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-md mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold text-gray-900">THAISLIPCHECK ยืนยันตัวตน</h1>
          <p className="text-[var(--brand-primary)] text-xl font-medium">ยืนยัน OTP</p>
        </div>

        {/* Demo mode notice */}
        {demoMode && (
          <div role="status" className="bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 rounded text-sm">
            <span className="font-semibold">โหมดเดโม:</span> ไม่ต้องยืนยัน OTP และไม่ต้องส่งเอกสารยืนยันตัวตน
          </div>
        )}

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {/* Form Fields */}
        <div className="space-y-4">
          {/* Phone Number */}
          <div>
            <Label htmlFor="phoneNumber" className="text-gray-700 font-medium flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
              เบอร์โทรศัพท์ <span className="text-red-500">*</span>
            </Label>
            <div className="mt-1 flex space-x-2">
              <Input
                id="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                placeholder="0812345678"
                maxLength={10}
                className="flex-1 border-[var(--brand-primary)] focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)]"
                disabled={loading}
              />
              {!demoMode && (
              <Button
                type="button"
                variant="outline"
                onClick={handleRequestOTP}
                disabled={loading || countdown > 0}
                className="border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white whitespace-nowrap bg-transparent"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : countdown > 0 ? (
                  `รอ ${countdown}s`
                ) : (
                  "ขอรหัส OTP"
                )}
              </Button>
              )}
            </div>
          </div>

          {/* OTP Code (hidden in demo mode) */}
          {!demoMode && (
          <div>
            <Label htmlFor="otpCode" className="text-gray-700 font-medium flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
              รหัส OTP <span className="text-red-500">*</span>
            </Label>
            <Input
              id="otpCode"
              type="text"
              value={formData.otpCode}
              onChange={(e) => handleInputChange("otpCode", e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              maxLength={6}
              className="mt-1 border-[var(--brand-primary)] focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)]"
              disabled={loading}
            />
          </div>
          )}

          {/* Password */}
          <div>
            <Label htmlFor="password" className="text-gray-700 font-medium flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
              รหัสผ่าน <span className="text-red-500">*</span>
            </Label>
            <div className="mt-1 relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                placeholder="ขั้นต่ำ 8 ตัวอักษร"
                className="border-[var(--brand-primary)] focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)] pr-10"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <Label htmlFor="confirmPassword" className="text-gray-700 font-medium flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
              ยืนยันรหัสผ่าน <span className="text-red-500">*</span>
            </Label>
            <div className="mt-1 relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                placeholder="กรอกรหัสผ่านอีกครั้ง"
                className="border-[var(--brand-primary)] focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)] pr-10"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Checkboxes */}
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <Checkbox
              id="agreeTerms"
              checked={formData.agreeTerms}
              onCheckedChange={(checked) => handleInputChange("agreeTerms", checked)}
              className="mt-1"
            />
            <Label htmlFor="agreeTerms" className="text-sm text-gray-700 leading-relaxed">
              ข้าพเจ้ายอมรับ{" "}
              <a href="/page/agreement" target="_blank" className="text-[var(--brand-primary)] hover:underline">
                ข้อกำหนดและเงื่อนไขในบริการ
              </a>
            </Label>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              id="agreePrivacy"
              checked={formData.agreePrivacy}
              onCheckedChange={(checked) => handleInputChange("agreePrivacy", checked)}
              className="mt-1"
            />
            <Label htmlFor="agreePrivacy" className="text-sm text-gray-700 leading-relaxed">
              ข้าพเจ้ารับทราบ{" "}
              <a href="/page/policy" target="_blank" className="text-[var(--brand-primary)] hover:underline">
                นโยบายความเป็นส่วนตัว
              </a>
            </Label>
          </div>
        </div>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={loading || !formData.agreeTerms || !formData.agreePrivacy}
          className="w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/90 text-white disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              กำลังดำเนินการ...
            </>
          ) : (
            "ยืนยันการสมัครสมาชิก"
          )}
        </Button>
      </div>
    </AuthLayout>
  )
}

